import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

// Note: Based on a private Varos interview
export const tiktokCaseStudy: ContentObject = {
    id: 'tiktok-genai-maintenance',
    kind: 'system', // Assuming Varos interview content is internal/system
    type: 'case-study',
    name: 'GenAI Streamlines Maintenance Software Development at TikTok',
    description:
        'This case study highlights how TikTok has successfully integrated GenAI into its software development lifecycle. By combining public GenAI models with tailored internal solutions—and incorporating advanced features such as automated Jira ticketing—the company has achieved significant productivity gains, enhanced code quality, and streamlined maintenance processes, all while effectively navigating challenges related to data security and legacy code integration. Varos’ private interview provided in-depth insights into both the successes and hurdles of this innovative approach.',
    url: null, // Private interview, no public URL
    scores: createScores({
        detailedMetrics: 9,
        relevanceToUseCase: 10,
        reliabilityOfSource: 10, // High reliability as it's a direct interview
        strategicAndTacticalInformation: 9,
        technicalImplementationDetails: 10,
        noVendorBias: 10, // Assuming interview provides balanced view
        relevanceToClient: 9,
    }),
    publishedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // Set to 3 days ago as an example
    markdown: `### 1. Source of Information

**Overview:**
The insights come from an expert interview conducted by Varos in a private setting. The discussion featured a senior software engineer from a major global video platform who has extensive experience in software engineering and has been instrumental in leveraging generative AI (GenAI) tools to drive both business and technical improvements. The interview, lasting nearly an hour, captures a candid conversation about day-to-day challenges and opportunities when using AI for development and maintenance work.

**Relevant Quote:**
> “I was a software engineer and responsible for the development of it. I have deep usage of these AI tools.”

---

### 2. Successes

**Routine Task Automation & Code Maintenance**

- **Modular Code Generation:**
 Instead of handling large coding tasks as a single unit, the team uses AI tools to generate small, modular functions.
 *“Every time you can ask it to write some separate function, that would be really good.”*
 This approach minimizes manual coding for repetitive tasks such as data processing, CSV conversions, or generating helper functions.

- **Automated Debugging & Bug Fixes:**
 **Initial Analysis & Brainstorming:** When bugs appear, engineers input error messages into tools like ChatGPT or Cursor to obtain initial analysis and suggestions.
 *“Sometimes if there is a bug, we can find the bug and just finish the code to remove the issue… it really helps us brainstorm.”*
 Although these tools might not always identify the exact root cause, they significantly reduce the time spent diagnosing and fixing issues.

- **Code Refactoring & Optimization:**
 **Refactoring Assistance:** Tools such as Cursor or the in-house “tree” scan the codebase and suggest optimizations or improvements, such as cleaning up redundant code and enhancing performance.
 **Automated Code Reviews:** AI is also used to generate summaries of code changes, streamlining the code review process and ensuring consistent documentation.

- **Routine Q&A and Support Automation:**
 **Handling Frequent Queries:** Routine operational questions and simple “how-to” queries can be answered automatically by fine‑tuned AI models. According to the engineer, up to 40% of these routine queries can be managed by the system, allowing senior engineers to focus on more complex issues.

- **Strategic Reallocation of Developer Time:**
 **Focusing on Innovation:**
 By automating many of the repetitive, maintenance-focused tasks, developers are able to devote more time to creating new features and driving business value.

---

### 3. Challenges

**Overview:**
While GenAI has delivered significant benefits, the engineer also highlighted several challenges that need to be managed carefully:

- **Managing Expectations:**
 There is a risk that stakeholders might overestimate what AI can accomplish, mistakenly believing that AI tools can fully replace human engineers. In reality, these tools are designed to augment human expertise, not substitute it. A deep understanding of the business context and strategic oversight remains essential.

- **Data Security and Confidentiality:**
 Large organizations must navigate stringent data security requirements. The risk of exposing sensitive code or proprietary algorithms forces companies to either build internal GenAI solutions or adopt highly secured environments, ensuring that critical data remains confidential.

- **Legacy Code Integration:**
 One of the more complex challenges is applying GenAI to legacy codebases. These systems often consist of decades-old code, built using outdated frameworks with minimal documentation. Although GenAI can generate suggestions for modularization and refactoring, the risk of introducing new errors remains high without careful human oversight. This necessitates a cautious, dual approach where legacy systems are maintained alongside gradual migration to more modern, AI-optimized architectures.

- **Operational Constraints in Production:**
 While GenAI can assist in diagnosing issues by analyzing error logs or code snippets, its direct integration into live production environments is limited due to operational constraints and the need to safeguard sensitive data.

- **Automated Ticketing with Jira:**
 The team leverages integration with Jira to automatically generate tickets when issues are detected. This automation minimizes manual overhead, ensuring that bugs or improvement opportunities are promptly logged and tracked. Although highly beneficial, it requires tight coordination between AI outputs and project management systems to maintain accuracy and relevance.

**Relevant Quotes:**
> “We shouldn’t have very high expectations. Some CEOs might think we can replace engineers with AI, but you still have to understand the business and the context, which is the tricky part.”

> “Legacy code is a whole different beast. The models can help suggest modular functions, but when it comes to systems built five or more years ago, without proper documentation, the risk of introducing new bugs is high.”

---

### 4. Technical Details

**Overview:**
The engineer provided insight into how GenAI is integrated into various technical aspects of the software development lifecycle:

- **Creative Insights and Content Analysis:**
 GenAI models are deployed to analyze multimedia content (text, images, and videos) to derive creative insights, identify trends, and assess competitor strategies across different markets.

- **Code Generation and Refactoring:**
 Large language models like ChatGPT (particularly version 4) are used to generate boilerplate code, suggest modular functions, and facilitate refactoring. This assists developers in exploring multiple technical solutions and optimizing the codebase.

- **Bug Fixing and Error Analysis:**
 In both development and live production environments, GenAI is utilized to analyze error logs and code snippets to brainstorm potential fixes. While this speeds up the debugging process, human oversight remains critical to ensure quality.

- **Internal Tooling and Integration:**
 The organization has developed in-house tools—comparable to leading external solutions—that are integrated into the development environment. These tools automatically track code changes, generate summaries for code reviews, and trigger automated ticket creation within Jira, ensuring smooth workflow management.

**Relevant Quote:**
> “For example, we use a lot of language models – ChatGPT, for instance – not only to write code but also to compare technical options, optimize our design, and even help brainstorm fixes when bugs occur.”

---

### 5. Results

**Overview:**
The integration of GenAI into the development process has yielded tangible improvements across several areas:

- **Increased Productivity:**
 The productivity boost is estimated at 30–40%, with project turnaround times reduced from a week to three or four working days. This accelerated cycle allows teams to focus on delivering new features and innovations.

- **Reduced Maintenance Work:**
 The time spent on routine maintenance has decreased by approximately 20–40%, enabling engineers to allocate more time to strategic and innovative tasks.

- **Streamlined Code Reviews and Issue Resolution:**
 Automated tools not only summarize code changes for efficient reviews but also integrate with Jira to automatically generate tickets for detected issues. This has improved the tracking and resolution of bugs, ensuring faster response times and better overall quality assurance.

**Relevant Quote:**
> “We can see that a project which would have cost one week can now be done in three or four working days. The entire cycle—from research to deployment—has become much faster.”

---

### 6. Vendors Used

**Overview:**
Several key vendors and tools play pivotal roles in the GenAI strategy employed by the organization:

- **Public GenAI Models:**
 The organization leverages widely available large language models such as ChatGPT—particularly version 4—which form the backbone of both creative and technical applications.

- **Multi-Modal AI Models:**
 Tools capable of processing not only text but also images and videos (referred to as DME in the conversation) are utilized to analyze diverse multimedia content and generate actionable insights.

- **Internal AI Tools:**
 To address data security concerns and customize functionalities, the company has developed its own internal tools that mirror some external offerings. These in-house solutions are integrated into the development environment for tasks such as code optimization, error detection, code review summarization, and automated ticket creation via Jira.

**Relevant Quote:**
> “I think ChatGPT is the best – I use version four because it gives the best reasoning. We also have our own internal tools that mimic what some external vendors offer, ensuring data security and better integration.”

---

This case study highlights how a leading global video platform has successfully integrated GenAI into its software development lifecycle. By combining public GenAI models with tailored internal solutions—and incorporating advanced features such as automated Jira ticketing—the company has achieved significant productivity gains, enhanced code quality, and streamlined maintenance processes, all while effectively navigating challenges related to data security and legacy code integration. Varos’ private interview provided in-depth insights into both the successes and hurdles of this innovative approach.
`,
    references: [
        {
            id: '1',
            name: 'Private Varos Interview with Senior Software Engineer',
            author: 'Varos',
            year: '2024', // Adjust year if needed
            publisher: 'TikTok (via Varos Interview)',
            url: null, // No public URL
        },
    ],
};
